import { useCallback, useEffect, useRef, useState } from "react";

import { safeSetItem } from "@/utils/localStorage";

/**
 * A safe wrapper around localStorage that handles QuotaExceededError gracefully.
 * Drop-in replacement for useLocalStorage from @uidotdev/usehooks.
 *
 * If localStorage is full, it will attempt to clear space and retry.
 * Falls back to in-memory storage if all else fails.
 * Makes sure the app runs without crashing even in edge cases.
 */
export function useSafeLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const initialValueRef = useRef(initialValue);
  const [storedValue, setStoredValue] = useState<T>(() => readFromStorage(key, initialValue));

  const valueRef = useRef(storedValue);
  valueRef.current = storedValue;

  useEffect(() => {
    setStoredValue(readFromStorage(key, initialValueRef.current));
  }, [key]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== key || e.storageArea !== window.localStorage) return;
      const parsed = e.newValue !== null ? parseJSON<T>(e.newValue, initialValueRef.current) : initialValueRef.current;
      valueRef.current = parsed;
      setStoredValue(parsed);
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [key]);

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(valueRef.current) : value;
        valueRef.current = valueToStore;
        setStoredValue(valueToStore);
        persistToStorage(key, valueToStore);
      } catch (error) {
        console.error(`[useSafeLocalStorage] Error in setValue for ${key}:`, error);
      }
    },
    [key],
  );

  return [storedValue, setValue];
}

function readFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const item = window.localStorage.getItem(key);
  return item !== null ? parseJSON(item, fallback) : fallback;
}

function parseJSON<T>(raw: string, fallback: T): T {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function persistToStorage<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  safeSetItem(window.localStorage, key, JSON.stringify(value));
}
